import React from 'react';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicPageHeader from '../components/PublicPageHeader';

const helpTopics = [
  {
    question: 'How do I add a child to my family?',
    answer: 'After signing up as a parent, go to Family Setup and invite your child using their email address or a join code.',
  },
  {
    question: 'How do chores and rewards work?',
    answer: 'Parents assign chores with point values. Kids complete chores and earn points (Earn-E coins) that can be redeemed for rewards you set.',
  },
  {
    question: 'How do I set up a subscription?',
    answer: 'Visit the Subscription page from your dashboard. Choose a monthly or annual plan to unlock all premium features.',
  },
  {
    question: 'Can I use Earn-E on multiple devices?',
    answer: 'Yes! Earn-E works on any device with a web browser, and we have Android and iOS apps available.',
  },
  {
    question: 'How do I reset my password?',
    answer: 'On the login page, tap "Forgot Password" and enter your email. You\'ll receive a reset link within a few minutes.',
  },
  {
    question: 'Is my family\'s data safe?',
    answer: 'Absolutely. We use Firebase Authentication and follow strict COPPA compliance guidelines. See our Privacy Policy and COPPA page for details.',
  },
];

const HelpPage = () => {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <PublicPageHeader title="Help" />
      <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h1" 
          component="h2" 
          gutterBottom 
          fontWeight="bold" 
          textAlign="center" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2,
            wordBreak: 'break-word',
            px: 2
          }}
        >
          Help Center
        </Typography>
        <Typography 
          variant="subtitle1" 
          textAlign="center" 
          color="text.secondary" 
          sx={{ 
            mb: 4,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 1, sm: 0 }
          }}
        >
          Find answers to common questions about Earn-E.
        </Typography>
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {helpTopics.map((topic, index) => (
            <Accordion 
              key={index} 
              disableGutters
              sx={{
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 auto',
                  minHeight: 48
                }
              }}
            >
              <AccordionSummary 
                expandIcon={
                  <ExpandMoreIcon 
                    sx={{ 
                      color: '#4CAF50',
                      transition: 'transform 0.3s',
                      '&.Mui-expanded': {
                        transform: 'rotate(180deg)'
                      }
                    }}
                  />
                }
                aria-controls={`help-panel-${index}`}
                id={`help-header-${index}`}
                sx={{
                  minHeight: 56,
                  px: 2,
                  bgcolor: 'rgba(76, 175, 80, 0.04)',
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(180deg)'
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    color: '#4CAF50'
                  },
                  '&:hover': {
                    bgcolor: 'rgba(76, 175, 80, 0.08)'
                  }
                }}
              >
                <Typography 
                  fontWeight="medium" 
                  sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    color: '#2E7D32'
                  }}
                >
                  {topic.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails 
                sx={{ px: 3, py: 2 }}
                id={`help-panel-${index}`}
                aria-labelledby={`help-header-${index}`}
              >
                <Typography 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6
                  }}
                >
                  {topic.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
        
        {/* Hint for mobile users */}
        <Typography 
          variant="caption" 
          textAlign="center" 
          color="text.secondary" 
          sx={{ 
            mt: 2, 
            display: { xs: 'block', sm: 'none' },
            fontSize: '0.75rem'
          }}
        >
          💡 Tap any question to reveal the answer
        </Typography>
      </Container>
    </Box>
  );
};

export default HelpPage;

